import { useEffect, useState } from 'react';
import { Calendar, Clock, Plus, TrendingUp, AlertTriangle, Loader2, Edit, Trash2, X, Save } from 'lucide-react';
import { loadingSlotService } from '../services/loadingSlotService';
import { warehouseService } from '../services/warehouseService';

interface LoadingSlotRecord {
  id: string;
  locationName: string;
  date: string;
  startTime: string;
  endTime: string;
  capacity: number;
  occupied: number;
  shipmentIds: string[];
}

interface SlotLocationOption {
  value: string;
  label: string;
}

const defaultFormData = {
  locationName: '',
  date: '',
  startTime: '',
  endTime: '',
  capacity: '',
  occupied: '0',
};

const mapSlot = (slot: any): LoadingSlotRecord => ({
  id: String(slot.id),
  locationName: slot.location_name || slot.locationName || '',
  date: (slot.date || '').split('T')[0],
  startTime: slot.start_time || slot.startTime || '',
  endTime: slot.end_time || slot.endTime || '',
  capacity: Number(slot.capacity || 0),
  occupied: Number(slot.occupied || 0),
  shipmentIds: slot.shipment_ids || slot.shipmentIds || [],
});

export function CapacityPlanning() {
  const [slots, setSlots] = useState<LoadingSlotRecord[]>([]);
  const [locationOptions, setLocationOptions] = useState<SlotLocationOption[]>([]);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSlot, setEditingSlot] = useState<LoadingSlotRecord | null>(null);
  const [formData, setFormData] = useState(defaultFormData);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    loadLocationOptions();
  }, []);

  useEffect(() => {
    loadSlots();
  }, [selectedDate]);

  const loadLocationOptions = async () => {
    try {
      const [warehousesRes, rampsRes] = await Promise.all([
        warehouseService.getWarehouses(),
        warehouseService.getRamps(),
      ]);

      const warehouseOptions = warehousesRes.success && warehousesRes.data
        ? warehousesRes.data.map((warehouse: any) => ({
            value: warehouse.name,
            label: warehouse.name,
          }))
        : [];

      const rampOptions = rampsRes.success && rampsRes.data
        ? rampsRes.data.map((ramp: any) => {
            const warehouseName = ramp.warehouse_name || ramp.warehouseName || '';
            const rampName = ramp.ramp_name || ramp.rampName || ramp.ramp_number || ramp.rampNumber || '';
            const label = [warehouseName, rampName].filter(Boolean).join(' - ');
            return {
              value: label,
              label,
            };
          }).filter((option: SlotLocationOption) => option.value)
        : [];

      const merged = [...rampOptions, ...warehouseOptions];
      const uniqueOptions = Array.from(new Map(merged.map((option) => [option.value, option])).values());
      setLocationOptions(uniqueOptions.sort((a, b) => a.label.localeCompare(b.label, 'tr')));
    } catch (err) {
      console.error('Error loading capacity locations:', err);
      setLocationOptions([]);
    }
  };

  const loadSlots = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await loadingSlotService.getLoadingSlots({ date: selectedDate });
      const allSlots = response.success && response.data ? response.data.map(mapSlot) : [];
      setSlots(allSlots.sort((a, b) => a.startTime.localeCompare(b.startTime)));
    } catch (err: any) {
      console.error('Error loading slots:', err);
      setError(err.message || 'Veriler yüklenirken hata oluştu');
    } finally {
      setIsLoading(false);
    }
  };

  const openModal = (slot?: LoadingSlotRecord) => {
    if (slot) {
      setEditingSlot(slot);
      setFormData({
        locationName: slot.locationName,
        date: slot.date,
        startTime: slot.startTime,
        endTime: slot.endTime,
        capacity: String(slot.capacity),
        occupied: String(slot.occupied),
      });
    } else {
      setEditingSlot(null);
      setFormData({ ...defaultFormData, date: selectedDate });
    }
    setIsModalOpen(true);
  };

  const resetForm = () => {
    setFormData(defaultFormData);
    setEditingSlot(null);
    setIsModalOpen(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      const payload = {
        locationName: formData.locationName,
        date: formData.date,
        startTime: formData.startTime,
        endTime: formData.endTime,
        capacity: parseInt(formData.capacity, 10),
        occupied: parseInt(formData.occupied, 10) || 0,
        shipmentIds: editingSlot?.shipmentIds || [],
      };

      const response = editingSlot
        ? await loadingSlotService.updateLoadingSlot(editingSlot.id, payload)
        : await loadingSlotService.createLoadingSlot(payload);

      if (!response.success) {
        throw new Error(response.message || 'Zaman dilimi kaydedilemedi');
      }

      resetForm();
      await loadSlots();
    } catch (err: any) {
      console.error('Error saving loading slot:', err);
      alert(err.message || 'Kayıt sırasında hata oluştu');
    } finally {
      setIsSaving(false);
    }
  };

  const deleteSlot = async (id: string) => {
    if (!confirm('Bu zaman dilimini silmek istediğinizden emin misiniz?')) return;

    try {
      const response = await loadingSlotService.deleteLoadingSlot(id);
      if (!response.success) {
        throw new Error(response.message || 'Zaman dilimi silinemedi');
      }
      await loadSlots();
    } catch (err: any) {
      console.error('Error deleting loading slot:', err);
      alert(err.message || 'Silme sırasında hata oluştu');
    }
  };

  const getUtilizationColor = (occupied: number, capacity: number) => {
    const percentage = capacity > 0 ? (occupied / capacity) * 100 : 0;
    if (percentage >= 90) return 'text-red-600 bg-red-50';
    if (percentage >= 70) return 'text-orange-600 bg-orange-50';
    if (percentage >= 50) return 'text-yellow-600 bg-yellow-50';
    return 'text-green-600 bg-green-50';
  };

  const totalCapacity = slots.reduce((sum, slot) => sum + slot.capacity, 0);
  const totalOccupied = slots.reduce((sum, slot) => sum + slot.occupied, 0);
  const utilizationRate = totalCapacity > 0 ? (totalOccupied / totalCapacity) * 100 : 0;
  const locations = [...new Set(slots.map((slot) => slot.locationName))];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Kapasite Planlama</h1>
          <p className="text-gray-500 mt-1">Yükleme rampa kapasitesi yönetimi</p>
        </div>
        <button onClick={() => openModal()} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"><Plus className="w-5 h-5" />Yeni Zaman Dilimi</button>
      </div>

      {isLoading && <div className="bg-white rounded-lg border border-gray-200 py-16 flex justify-center"><div className="flex flex-col items-center gap-3"><Loader2 className="w-8 h-8 text-blue-600 animate-spin" /><p className="text-gray-500">Zaman dilimleri yükleniyor...</p></div></div>}
      {error && !isLoading && <div className="bg-white rounded-lg border border-red-200 py-12 text-center"><p className="text-red-600 mb-4">{error}</p><button onClick={loadSlots} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Tekrar Dene</button></div>}

      {!isLoading && !error && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-white rounded-lg shadow p-6"><div className="flex items-center justify-between mb-2"><h3 className="text-sm font-medium text-gray-500">Toplam Kapasite</h3><Calendar className="w-5 h-5 text-blue-600" /></div><p className="text-3xl font-bold text-gray-900">{totalCapacity}</p><p className="text-sm text-gray-500 mt-1">Araç/gün</p></div>
            <div className="bg-white rounded-lg shadow p-6"><div className="flex items-center justify-between mb-2"><h3 className="text-sm font-medium text-gray-500">Kullanılan</h3><TrendingUp className="w-5 h-5 text-green-600" /></div><p className="text-3xl font-bold text-gray-900">{totalOccupied}</p><p className="text-sm text-gray-500 mt-1">Planlanan araç</p></div>
            <div className="bg-white rounded-lg shadow p-6"><div className="flex items-center justify-between mb-2"><h3 className="text-sm font-medium text-gray-500">Doluluk Oranı</h3><Clock className="w-5 h-5 text-purple-600" /></div><p className="text-3xl font-bold text-gray-900">{utilizationRate.toFixed(0)}%</p><p className="text-sm text-gray-500 mt-1">Kapasite kullanımı</p></div>
            <div className="bg-white rounded-lg shadow p-6"><div className="flex items-center justify-between mb-2"><h3 className="text-sm font-medium text-gray-500">Uyarılar</h3><AlertTriangle className="w-5 h-5 text-orange-600" /></div><p className="text-3xl font-bold text-gray-900">{slots.filter((slot) => slot.occupied >= slot.capacity * 0.9).length}</p><p className="text-sm text-gray-500 mt-1">Dolmak üzere</p></div>
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center gap-4">
              <Calendar className="w-5 h-5 text-gray-400" />
              <input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              <div className="flex-1 flex items-center gap-2">
                <button onClick={() => { const date = new Date(selectedDate); date.setDate(date.getDate() - 1); setSelectedDate(date.toISOString().split('T')[0]); }} className="px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">Önceki Gün</button>
                <button onClick={() => setSelectedDate(new Date().toISOString().split('T')[0])} className="px-3 py-2 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200">Bugün</button>
                <button onClick={() => { const date = new Date(selectedDate); date.setDate(date.getDate() + 1); setSelectedDate(date.toISOString().split('T')[0]); }} className="px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">Sonraki Gün</button>
              </div>
            </div>
          </div>

          {locations.length > 0 ? (
            <div className="space-y-6">
              {locations.map((location) => {
                const locationSlots = slots.filter((slot) => slot.locationName === location);
                return (
                  <div key={location} className="bg-white rounded-lg shadow">
                    <div className="p-6 border-b border-gray-200"><h2 className="text-lg font-semibold text-gray-900">{location}</h2></div>
                    <div className="p-6"><div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">{locationSlots.map((slot) => {
                      const utilizationPercentage = slot.capacity > 0 ? (slot.occupied / slot.capacity) * 100 : 0;
                      return (
                        <div key={slot.id} className="border-2 border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors">
                          <div className="flex items-center justify-between mb-3"><div className="flex items-center gap-2"><Clock className="w-4 h-4 text-gray-400" /><span className="font-medium text-gray-900">{slot.startTime} - {slot.endTime}</span></div><div className="flex items-center gap-1"><button onClick={() => openModal(slot)} className="p-1 text-blue-600 hover:bg-blue-50 rounded"><Edit className="w-4 h-4" /></button><button onClick={() => deleteSlot(slot.id)} className="p-1 text-red-600 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button></div></div>
                          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${getUtilizationColor(slot.occupied, slot.capacity)}`}><span>{slot.occupied} / {slot.capacity}</span></div>
                          <div className="mt-3"><div className="w-full bg-gray-200 rounded-full h-2"><div className={`h-2 rounded-full transition-all ${utilizationPercentage >= 90 ? 'bg-red-500' : utilizationPercentage >= 70 ? 'bg-orange-500' : utilizationPercentage >= 50 ? 'bg-yellow-500' : 'bg-green-500'}`} style={{ width: `${Math.min(utilizationPercentage, 100)}%` }} /></div><p className="text-xs text-gray-500 mt-1">{utilizationPercentage.toFixed(0)}% Doluluk</p></div>
                          {slot.occupied >= slot.capacity && <div className="mt-2 text-xs text-red-600 font-medium">Kapasite dolu</div>}
                        </div>
                      );
                    })}</div></div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow p-12 text-center"><Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" /><h3 className="text-lg font-semibold text-gray-700 mb-2">Bu Tarihte Zaman Dilimi Yok</h3><p className="text-sm text-gray-500 mb-4">Yeni zaman dilimi ekleyerek başlayın</p><button onClick={() => openModal()} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"><Plus className="w-5 h-5" />Zaman Dilimi Ekle</button></div>
          )}
        </>
      )}

      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between"><h2 className="text-xl font-semibold text-gray-900">{editingSlot ? 'Zaman Dilimini Düzenle' : 'Yeni Zaman Dilimi Ekle'}</h2><button onClick={resetForm} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button></div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
               <div><label className="block text-sm font-medium text-gray-700 mb-1">Lokasyon *</label><select required value={formData.locationName} onChange={(e) => setFormData({ ...formData, locationName: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"><option value="">Seçiniz</option>{locationOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Tarih *</label><input type="date" required value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" /></div>
              <div className="grid grid-cols-2 gap-4"><div><label className="block text-sm font-medium text-gray-700 mb-1">Başlangıç *</label><input type="time" required value={formData.startTime} onChange={(e) => setFormData({ ...formData, startTime: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" /></div><div><label className="block text-sm font-medium text-gray-700 mb-1">Bitiş *</label><input type="time" required value={formData.endTime} onChange={(e) => setFormData({ ...formData, endTime: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" /></div></div>
              <div className="grid grid-cols-2 gap-4"><div><label className="block text-sm font-medium text-gray-700 mb-1">Kapasite (Araç) *</label><input type="number" required min="1" value={formData.capacity} onChange={(e) => setFormData({ ...formData, capacity: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="Aynı anda kaç araç" /></div><div><label className="block text-sm font-medium text-gray-700 mb-1">Kullanılan *</label><input type="number" required min="0" value={formData.occupied} onChange={(e) => setFormData({ ...formData, occupied: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="Planlanan araç" /></div></div>
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200"><button type="button" onClick={resetForm} className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">İptal</button><button type="submit" disabled={isSaving} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-60">{isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}{editingSlot ? 'Güncelle' : 'Ekle'}</button></div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
